import { memo, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Brain, Edit2, Trash2, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button } from '@caruso/ui';

interface ResponseOption {
  id: string;
  label: string;
  keywords: string[];
}

interface SmartResponseData {
  responseOptions?: ResponseOption[];
  useAI?: boolean;
  aiPrompt?: string;
  waitTimeoutMinutes?: number;
}

export const SmartResponseNode = memo(({ data, id }: NodeProps<SmartResponseData>) => {
  const { deleteElements } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [options, setOptions] = useState<ResponseOption[]>(data.responseOptions || []);
  const [useAI, setUseAI] = useState(data.useAI || false);
  const [aiPrompt, setAiPrompt] = useState(data.aiPrompt || '');
  const [timeout, setTimeout] = useState(data.waitTimeoutMinutes || 1440);

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleAddOption = () => {
    const newOption: ResponseOption = {
      id: `opcao_${Date.now()}`,
      label: '',
      keywords: [],
    };
    setOptions([...options, newOption]);
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleUpdateOption = (index: number, field: keyof ResponseOption, value: any) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setOptions(newOptions);
  };

  const handleUpdateKeywords = (index: number, keywordsText: string) => {
    const keywords = keywordsText
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);
    handleUpdateOption(index, 'keywords', keywords);
  };

  const handleSave = () => {
    data.responseOptions = options;
    data.useAI = useAI;
    data.aiPrompt = aiPrompt;
    data.waitTimeoutMinutes = timeout;
    setIsEditing(false);
  };

  const handleCancel = () => {
    setOptions(data.responseOptions || []);
    setUseAI(data.useAI || false);
    setAiPrompt(data.aiPrompt || '');
    setTimeout(data.waitTimeoutMinutes || 1440);
    setIsEditing(false);
  };

  return (
    <>
      <div className="bg-gradient-to-br from-purple-900/80 to-blue-900/80 border-2 border-purple-500/60 rounded-lg p-4 min-w-[280px] shadow-lg">
        <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-purple-500" />

        <div className="flex items-center gap-2 mb-3">
          <Brain className="w-5 h-5 text-purple-300" />
          <span className="font-bold text-white">Resposta Inteligente</span>
          {useAI && (
            <span className="ml-1 px-2 py-0.5 bg-purple-500/30 text-purple-200 text-xs rounded-full border border-purple-400/30">
              IA
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="text-purple-300 hover:text-white transition-colors p-1"
              title="Editar resposta inteligente"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className="text-red-400 hover:text-red-300 transition-colors p-1"
              title="Deletar nó"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {options.length > 0 ? (
            <div className="pt-2 space-y-1">
              <div className="text-xs text-purple-200 font-semibold mb-1">Opções ({options.length}):</div>
              {options.slice(0, 3).map((option, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-white/80 bg-black/20 rounded px-2 py-1 border border-purple-500/10">
                  <span className="w-5 h-5 flex items-center justify-center bg-purple-500/30 rounded-full text-purple-200 font-bold text-[10px]">
                    {idx + 1}
                  </span>
                  <span className="flex-1 truncate">{option.label || 'Sem título'}</span>
                </div>
              ))}
              {options.length > 3 && (
                <div className="text-xs text-purple-300/60 text-center">
                  +{options.length - 3} mais
                </div>
              )}
            </div>
          ) : (
            <div className="text-white/50 text-sm text-center py-3">
              Clique para configurar opções
            </div>
          )}
        </div>

        {/* Handles dinâmicos para cada opção */}
        {options.map((option, idx) => (
          <Handle
            key={option.id}
            type="source"
            position={Position.Right}
            id={option.id}
            style={{ top: `${50 + (idx * 30)}px` }}
            className="w-3 h-3 !bg-purple-500"
          />
        ))}
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-neutral-900 border-purple-500/20 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              Aguardar Resposta Inteligente
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/70">
              Configure as opções de resposta. O sistema aguardará a resposta do usuário (enviada pelo nó anterior) e identificará qual caminho seguir.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-200">
                <strong>💡 Dica:</strong> Adicione um nó de <strong>Texto</strong> ou <strong>Pergunta</strong> antes deste nó para enviar a mensagem ao cliente.
              </p>
            </div>

            {/* Opções de Resposta */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-brand-ivory">
                  Opções de Resposta
                </label>
                <Button
                  onClick={handleAddOption}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar Opção
                </Button>
              </div>

              <div className="space-y-3">
                {options.map((option, idx) => (
                  <div key={option.id} className="p-3 bg-neutral-800/50 border border-neutral-700 rounded-lg">
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 flex items-center justify-center bg-purple-500/30 rounded-full text-purple-200 font-bold text-xs flex-shrink-0 mt-1">
                        {idx + 1}
                      </div>
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={option.label}
                          onChange={(e) => handleUpdateOption(idx, 'label', e.target.value)}
                          placeholder="Ex: Suporte técnico"
                          className="w-full p-2 bg-neutral-900/50 border border-neutral-600 text-white placeholder:text-neutral-500 rounded focus:outline-none focus:ring-1 focus:ring-purple-500/50 text-sm"
                        />
                        <input
                          type="text"
                          value={option.keywords.join(', ')}
                          onChange={(e) => handleUpdateKeywords(idx, e.target.value)}
                          placeholder="Palavras-chave: suporte, ajuda, problema (separadas por vírgula)"
                          className="w-full p-2 bg-neutral-900/50 border border-neutral-600 text-white placeholder:text-neutral-500 rounded focus:outline-none focus:ring-1 focus:ring-purple-500/50 text-sm"
                        />
                      </div>
                      <button
                        onClick={() => handleRemoveOption(idx)}
                        className="text-red-400 hover:text-red-300 transition-colors p-1 flex-shrink-0"
                        title="Remover opção"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {options.length === 0 && (
                  <div className="text-center py-6 text-neutral-500 text-sm">
                    Nenhuma opção adicionada. Clique em "Adicionar Opção" para começar.
                  </div>
                )}
              </div>
            </div>

            {/* IA Configuration */}
            <div className="border-t border-neutral-700 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="text-sm font-medium text-brand-ivory flex items-center gap-2">
                    <Brain className="w-4 h-4 text-purple-400" />
                    Usar Inteligência Artificial
                  </label>
                  <p className="text-xs text-neutral-400 mt-1">
                    A IA analisará a resposta do usuário e decidirá o melhor caminho automaticamente
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useAI}
                    onChange={(e) => setUseAI(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-neutral-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>

              {useAI && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-brand-ivory mb-2">
                    Prompt Customizado (Opcional)
                  </label>
                  <textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    className="w-full p-3 bg-neutral-800/50 border border-neutral-700 text-white placeholder:text-neutral-500 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                    rows={3}
                    placeholder="Deixe em branco para usar o prompt padrão, ou customize para refinar a análise da IA..."
                  />
                </div>
              )}
            </div>

            {/* Timeout */}
            <div>
              <label className="block text-sm font-medium text-brand-ivory mb-2">
                Tempo Máximo de Espera (minutos)
              </label>
              <input
                type="number"
                value={timeout}
                onChange={(e) => setTimeout(parseInt(e.target.value) || 1440)}
                min="1"
                className="w-full p-3 bg-neutral-800/50 border border-neutral-700 text-white placeholder:text-neutral-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              />
              <p className="text-xs text-neutral-400 mt-1">
                Padrão: 1440 minutos (24 horas)
              </p>
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t border-neutral-700">
              <Button
                onClick={handleCancel}
                variant="outline"
                className="border-neutral-700 text-white hover:bg-neutral-800"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold"
              >
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

SmartResponseNode.displayName = 'SmartResponseNode';
